import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Collapse,
  IconButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  AccountBalance as AccountBalanceIcon,
  CardGiftcard as CardGiftcardIcon,
  FileDownload as FileDownloadIcon,
  AddCard as AddFundsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import AddFundsTab from '@shared/AddFundsTab';
import AccountHistoryHeader from '@shared/AccountHistoryHeader';

// Sort column and direction types
type SortColumn = 'date' | 'type' | 'postage' | 'attached' | 'reserved' | 'balance' | 'fees' | 'subject';
type SortDirection = 'asc' | 'desc';
import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { accountService, PromoTransaction } from '../services/accountService';
import { fmtDateTime, fmtDateTimeShort } from '@shared/formatDate';
import { getTransactionListingDate, getTransactionListingTime } from '@shared/transactionListingDate';
import { getVisibleTabKeys } from '../services/configService';
import { Transaction, AccountHistoryFilter } from '../types';
import { useAuth } from '../hooks/useAuth';
import TransactionDetailsDialog from '../components/shared/TransactionDetailsDialog';
import PromoTransactionDetailsDialog from '../components/shared/PromoTransactionDetailsDialog';
import TransactionSearchFilter, { 
  useTransactionSearchFilter,
  defaultRegularTransactionFilter,
  defaultPromoTransactionFilter,
  defaultSort,
  // exportToCsv - available but not currently used
} from '../components/shared/TransactionSearchFilter';

// Minimum set at component level; see dynamic override inside the component
const BASE_MIN_FUNDS_AMOUNT   = 5;
const VENDOR_MIN_FUNDS_AMOUNT = 25;

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2004 }, (_, i) => currentYear - i);

const getTypeColor = (type: string) => {
  switch (type) {
    case 'Credit':
    case 'Credit Purchase':
    case 'Message': // Received message (credit)
    case 'STRIPE_DEPOSIT': // Stripe credit card/ACH deposit
    case 'STRIPE_AUTO_REFILL': // Auto-refill deposit
    case 'STRIPE_REFUND': // Refund returned to account
      return 'success';
    case 'Debit':
    case 'Card Debit':
    case 'Pending Delivery': // Pending message (reserved/debit)
    case 'In Inbox': // Delivered to inbox but not read yet
    case 'STRIPE_WITHDRAWAL': // Stripe payout/withdrawal
      return 'warning';
    case 'Marketing Message':
      return 'info';
    case 'Ad View':
      return 'info';
    default:
      return 'default';
  }
};

// Format transaction type for display
const formatTypeDisplay = (type: string) => {
  switch (type) {
    case 'STRIPE_DEPOSIT':
      return 'Deposit';
    case 'STRIPE_AUTO_REFILL':
      return 'Auto-Refill';
    case 'STRIPE_WITHDRAWAL':
      return 'Withdrawal';
    case 'STRIPE_REFUND':
      return 'Refund';
    case 'Card Debit':
      return 'Card Debit';
    default:
      return type;
  }
};

// Format amount with color based on whether it's a credit (green) or debit/reserve (red)
const formatAmountWithColor = (amount: number, action?: string) => {
  // Reserved or Debit actions show as red, Credit shows as green
  const isDebitOrReserve = action === 'Reserved' || action === 'Debit' || amount < 0;
  const absAmount = Math.abs(amount);
  const color = isDebitOrReserve ? '#d32f2f' : '#2e7d32';
  const prefix = isDebitOrReserve ? '-' : '+';
  
  return (
    <span style={{ color, fontWeight: 600 }}>
      {prefix}${absAmount.toFixed(2)}
    </span>
  );
};

// Format fees in fractional cents (4 decimal places) - available for detailed fee display
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatFractionalCents = (amount: number) => {
  if (amount === 0) return '-';
  return `$${amount.toFixed(4)}`;
};

// Truncate subject to 30 characters with ellipsis
const truncateSubject = (subject: string | undefined, maxLength: number = 30) => {
  if (!subject) return '-';
  if (subject.length <= maxLength) return subject;
  return subject.substring(0, maxLength) + '...';
};

// Legacy aliases — now delegating to shared formatDate utility
const formatDateOnly = (dateString: string) => fmtDateTimeShort(dateString);
const formatFullDateTime = (dateString: string) => fmtDateTime(dateString);

const AccountHistory: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, hasAdvancedMailbox, hasVendorCredentials, hasMarketerCredentials } = useAuth();
  const MIN_FUNDS_AMOUNT = (hasVendorCredentials || hasMarketerCredentials) ? VENDOR_MIN_FUNDS_AMOUNT : BASE_MIN_FUNDS_AMOUNT;
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000,
    enabled: !!user?.username,
  });

  // Tab state - 0 = first visible sub-tab, 1 = second (Account Balance, Vendor Credits)
  const [activeTab, setActiveTab] = useState(0);

  // Vendor credit summary collapse state (persisted)
  const [vcSummaryOpen, setVcSummaryOpen] = useState(() => {
    try { return localStorage.getItem('vendorCreditsSummaryCollapsed') !== 'true'; }
    catch { return true; }
  });
  const toggleVcSummary = useCallback(() => {
    setVcSummaryOpen(prev => {
      const next = !prev;
      try { localStorage.setItem('vendorCreditsSummaryCollapsed', next ? 'false' : 'true'); } catch {}
      return next;
    });
  }, []);

  // Whether to show vendors with zero balance in the summary table
  const [showZeroBalanceVendors, setShowZeroBalanceVendors] = useState(false);
  
  // Account history state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [year, setYear] = useState<number>(currentYear);
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Username filter state
  const [filterByMyAlias, setFilterByMyAlias] = useState<string>('');
  const [filterByOtherParty, setFilterByOtherParty] = useState<string>('');
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);


  // Handle sort column click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc'); // Default to descending for new column
    }
  };
  
  // Handle CSV export
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await accountService.exportHistory({
        year,
        month,
        filterByOtherParty: filterByOtherParty || undefined,
        filterByMyAlias: filterByMyAlias || undefined,
      });
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Sort transactions client-side
  const sortTransactions = (transactions: Transaction[] | undefined) => {
    if (!transactions) return [];
    
    return [...transactions].sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (sortColumn) {
        case 'date':
          aVal = getTransactionListingTime(a);
          bVal = getTransactionListingTime(b);
          break;
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          break;
        case 'postage':
          aVal = a.requiredPostage ?? a.postage ?? 0;
          bVal = b.requiredPostage ?? b.postage ?? 0;
          break;
        case 'attached':
          aVal = a.attachedFunds ?? a.attachedAmount ?? 0;
          bVal = b.attachedFunds ?? b.attachedAmount ?? 0;
          break;
        case 'reserved':
          aVal = a.reservedFunds ?? 0;
          bVal = b.reservedFunds ?? 0;
          break;
        case 'balance':
          // Use Actual Account Balance (ignore Available Balance)
          aVal = a.actualBalanceAfter ?? (a as any).balanceAfter ?? 0;
          bVal = b.actualBalanceAfter ?? (b as any).balanceAfter ?? 0;
          break;
        case 'fees':
          aVal = (a.postageSystemFee ?? 0) + (a.attachedFundsFee ?? 0);
          bVal = (b.postageSystemFee ?? 0) + (b.attachedFundsFee ?? 0);
          break;
        case 'subject':
          aVal = a.subject || '';
          bVal = b.subject || '';
          break;
        default:
          return 0;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const filter: AccountHistoryFilter = {
    year,
    month,
    page: page + 1,
    pageSize: rowsPerPage,
    filterByMyAlias: filterByMyAlias || undefined,
    filterByOtherParty: filterByOtherParty || undefined,
  };

  // Include username in query keys so each user gets their own cached data
  const { data: history, isLoading, error, refetch } = useQuery({
    queryKey: ['accountHistory', user?.username, filter],
    queryFn: () => accountService.getHistory(filter),
    enabled: !!user?.username,
  });

  const { data: balance } = useQuery({
    queryKey: ['accountBalance', user?.username],
    queryFn: () => accountService.getBalance(),
    staleTime: 5000,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    enabled: !!user?.username,
  });

  const addFundsWithBalanceRefresh = useCallback(async (...args: Parameters<typeof accountService.addFunds>) => {
    const result = await accountService.addFunds(...args);
    if (result?.success) {
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      queryClient.invalidateQueries({ queryKey: ['accountHistory', user?.username] });
      queryClient.invalidateQueries({ queryKey: ['promoHistory', user?.username] });
    }
    return result;
  }, [queryClient, user?.username]);

  // Fetch user's aliases for filter dropdown
  const { data: userAliases } = useQuery({
    queryKey: ['userAliases', user?.username],
    queryFn: () => accountService.getAliases(),
    enabled: !!user?.username,
  });

  // Promo credit transaction history - always fetch to determine tab visibility
  const { data: promoHistory, isLoading: promoLoading, refetch: refetchPromo } = useQuery({
    queryKey: ['promoHistory', user?.username],
    queryFn: () => accountService.getPromoHistory(100),
    enabled: !!user?.username,
  });
  
  // Vendor Credits tab is always visible so users can always check their promo
  // credit status even when balance is zero.
  const shouldShowPromoTab = true;

  // Sub-tab visibility: admin "Visible Tabs" + advanced users see all
  const showAccountBalance = hasAdvancedMailbox || (visibleTabKeys.length > 0 ? visibleTabKeys.includes('account-history/account-balance') : true);
  const showVendorCredits = (hasAdvancedMailbox || (visibleTabKeys.length > 0 ? visibleTabKeys.includes('account-history/vendor-credits') : true)) && shouldShowPromoTab;
  const visibleSubTabs = [showAccountBalance && 'account-balance', showVendorCredits && 'vendor-credits', 'add-funds'].filter(Boolean) as string[];
  const currentSubTab = visibleSubTabs[activeTab] ?? visibleSubTabs[0];

  useEffect(() => {
    if (visibleSubTabs.length > 0 && activeTab >= visibleSubTabs.length) setActiveTab(0);
  }, [visibleSubTabs.length, activeTab]);


  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleRowClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedTransaction(null);
  };

  const handleViewMessage = useCallback((mailId: string) => {
    setDetailsDialogOpen(false);
    setSelectedTransaction(null);
    navigate(`/messages/${mailId}`);
  }, [navigate]);

  // Promo transaction dialog state
  const [selectedPromoTransaction, setSelectedPromoTransaction] = useState<PromoTransaction | null>(null);
  const [promoDetailsDialogOpen, setPromoDetailsDialogOpen] = useState(false);

  const handlePromoRowClick = (transaction: PromoTransaction) => {
    setSelectedPromoTransaction(transaction);
    setPromoDetailsDialogOpen(true);
  };

  const handleOpenLinkedRegularDetail = (transactionId: string) => {
    setPromoDetailsDialogOpen(false);
    setSelectedPromoTransaction(null);
    setSelectedTransaction({ transactionId } as Transaction);
    setDetailsDialogOpen(true);
  };

  const handleClosePromoDetails = () => {
    setPromoDetailsDialogOpen(false);
    setSelectedPromoTransaction(null);
  };

  // Search filter hook for regular transactions
  const regularFilter = useTransactionSearchFilter();
  
  // Search filter hook for promo transactions
  const promoFilter = useTransactionSearchFilter();

  // Get unique transaction types from current data
  const regularTransactionTypes = useMemo(() => {
    const types = new Set<string>();
    (history?.transactions || []).forEach(tx => {
      if (tx.type) types.add(tx.type);
      if (tx.rawType) types.add(tx.rawType);
    });
    return Array.from(types).map(t => ({ value: t, label: t }));
  }, [history?.transactions]);

  const promoTransactionTypes = useMemo(() => {
    const types = new Set<string>();
    (promoHistory || []).forEach(tx => {
      if (tx.transactionType) types.add(tx.transactionType);
    });
    return Array.from(types).map(t => ({ value: t, label: t.replace(/_/g, ' ') }));
  }, [promoHistory]);

  // Apply filters and sorting to regular transactions
  const filteredAndSortedTransactions = useMemo(() => {
    let result = history?.transactions || [];
    
    // Apply search filter if expanded
    if (regularFilter.isExpanded || regularFilter.searchQuery || regularFilter.typeFilter) {
      result = result.filter(tx => 
        defaultRegularTransactionFilter(
          tx as any,
          regularFilter.searchQuery,
          regularFilter.dateRangeStart,
          regularFilter.dateRangeEnd,
          regularFilter.typeFilter
        )
      );
    }
    
    // Apply sorting from TransactionSearchFilter (if sortColumn is set from there)
    if (regularFilter.sortColumn) {
      result = defaultSort(result, regularFilter.sortColumn, regularFilter.sortDirection, getTransactionListingDate);
    } else {
      // Use the existing sortColumn/sortDirection
      result = sortTransactions(result);
    }
    
    return result;
  }, [history?.transactions, regularFilter.searchQuery, regularFilter.dateRangeStart, regularFilter.dateRangeEnd, regularFilter.typeFilter, regularFilter.sortColumn, regularFilter.sortDirection, sortColumn, sortDirection]);

  // Compute running balance for promo transactions (chronological order)
  const promoRunningBalances = useMemo(() => {
    const map = new Map<string, number>();
    if (!promoHistory?.length) return map;

    const chronological = [...promoHistory].sort((a, b) =>
      new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
    );

    let running = 0;
    for (const tx of chronological) {
      running += tx.amount;
      map.set(tx.transactionId, Math.max(0, parseFloat(running.toFixed(4))));
    }
    return map;
  }, [promoHistory]);

  // Build per-sponsor summaries from transaction history (running balance) + live balance for expiry only
  const sponsorSummaries = useMemo(() => {
    const groups = new Map<string, {
      sponsor: string;
      availableBalance: number;
      totalGranted: number;
      totalSpent: number;
      soonestExpiry: number | null;
      creditCount: number;
      lastActivity: Date;
    }>();

    // Sort chronologically so the running balance per sponsor is computed correctly
    const sorted = [...(promoHistory || [])].sort((a, b) =>
      new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
    );

    // Running balance per sponsor derived entirely from transaction history.
    // This is self-consistent with totalGranted/totalSpent so the expired formula is always correct.
    const runningBySponsor = new Map<string, number>();
    for (const tx of sorted) {
      const s = tx.sponsorUsername;
      if (!groups.has(s)) {
        groups.set(s, {
          sponsor: s, availableBalance: 0, totalGranted: 0,
          totalSpent: 0, soonestExpiry: null,
          creditCount: 0, lastActivity: new Date(0),
        });
      }
      const g = groups.get(s)!;
      const txDate = new Date(tx.transactionDate);
      if (txDate > g.lastActivity) g.lastActivity = txDate;

      const prev = runningBySponsor.get(s) ?? 0;
      const next = Math.max(0, parseFloat((prev + tx.amount).toFixed(4)));
      runningBySponsor.set(s, next);
      g.availableBalance = next; // last transaction's running balance = current balance

      if (tx.transactionType === 'GRANT' || tx.transactionType === 'ADDITION' || tx.transactionType === 'REFUND') {
        g.totalGranted += Math.abs(tx.amount);
      } else if (tx.transactionType === 'SPEND') {
        g.totalSpent += Math.abs(tx.amount);
      }
      // EXPIRE transactions reduce the running balance (already handled above).
      // Total Expired is derived in the table: totalGranted − availableBalance − totalSpent.
    }

    // Merge expiry info from live balance only (not balance amounts)
    for (const credit of (balance?.promoCredits || [])) {
      const s = credit.sponsorUsername;
      if (groups.has(s)) {
        const g = groups.get(s)!;
        g.creditCount += 1;
        if (credit.daysUntilExpiration != null) {
          if (g.soonestExpiry === null || credit.daysUntilExpiration < g.soonestExpiry) {
            g.soonestExpiry = credit.daysUntilExpiration;
          }
        }
      }
    }

    return Array.from(groups.values()).sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
  }, [promoHistory, balance?.promoCredits]);

  // Apply filters and sorting to promo transactions
  const filteredAndSortedPromoTransactions = useMemo(() => {
    let result = promoHistory || [];
    
    // Apply search filter if expanded
    if (promoFilter.isExpanded || promoFilter.searchQuery || promoFilter.typeFilter) {
      result = result.filter(tx => 
        defaultPromoTransactionFilter(
          tx as any,
          promoFilter.searchQuery,
          promoFilter.dateRangeStart,
          promoFilter.dateRangeEnd,
          promoFilter.typeFilter
        )
      );
    }
    
    // Apply sorting
    if (promoFilter.sortColumn) {
      result = defaultSort(result, promoFilter.sortColumn, promoFilter.sortDirection, (tx) => tx.transactionDate);
    } else {
      // Default sort by date descending
      result = [...result].sort((a, b) => 
        new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
      );
    }
    
    return result;
  }, [promoHistory, promoFilter.searchQuery, promoFilter.dateRangeStart, promoFilter.dateRangeEnd, promoFilter.typeFilter, promoFilter.sortColumn, promoFilter.sortDirection]);

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load account history. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <AccountHistoryHeader titleVariant="h4" />
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {showAccountBalance && (
            <Tab 
              icon={<AccountBalanceIcon />} 
              iconPosition="start" 
              label="Account Balance" 
            />
          )}
          {showVendorCredits && (
            <Tab 
              icon={<CardGiftcardIcon />} 
              iconPosition="start" 
              label={`Vendor Credits${balance?.totalPromoBalance ? ` ($${balance.totalPromoBalance.toFixed(2)})` : ''}`}
            />
          )}
          <Tab icon={<AddFundsIcon />} iconPosition="start" label="Add Credits" />
        </Tabs>
      </Paper>

      {/* Account Balance sub-tab */}
      {currentSubTab === 'account-balance' && (
        <>
          {/* Balance Card */}
      {balance && (
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="overline">Account Balance</Typography>
                <Typography variant="h4">${balance.actualBalance.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="overline">Reserved Funds</Typography>
                <Typography variant="h5">${balance.pendingBalance.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="overline">Available Funds</Typography>
                <Typography variant="h5">${balance.availableBalance.toFixed(2)}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TransactionSearchFilter
          transactions={history?.transactions || []}
          transactionTypes={regularTransactionTypes}
          {...regularFilter.filterProps}
          csvFilename="account_transactions"
          csvHeaders={['Transaction ID', 'Date', 'Type', 'Action', 'Subject', 'Postage', 'Attached Credits', 'Reserved', 'Fees', 'Balance After']}
          getCsvRow={(tx: any) => [
            tx.transactionId,
            getTransactionListingDate(tx),
            tx.type || '',
            tx.action || '',
            tx.subject || '',
            (tx.requiredPostage ?? tx.postage ?? 0).toString(),
            (tx.attachedFunds ?? tx.attachedAmount ?? 0).toString(),
            (tx.reservedFunds ?? 0).toString(),
            ((tx.postageSystemFee ?? 0) + (tx.attachedFundsFee ?? 0)).toString(),
            (tx.actualBalanceAfter ?? tx.balanceAfter ?? 0).toString()
          ]}
          filterFn={(tx: any, query, start, end, type) => 
            defaultRegularTransactionFilter(tx, query, start, end, type)
          }
        />
      </Paper>

      {/* Period Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 120px', minWidth: 120 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={year}
                label="Year"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 120px', minWidth: 120 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={month}
                label="Month"
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* My Alias Filter */}
          {userAliases && userAliases.length > 1 && (
            <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>My Alias</InputLabel>
                <Select
                  value={filterByMyAlias}
                  label="My Alias"
                  onChange={(e) => { setFilterByMyAlias(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">All Aliases</MenuItem>
                  {userAliases.map((alias) => (
                    <MenuItem key={alias} value={alias}>
                      {alias}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {/* Other Party Filter */}
          <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Other Party</InputLabel>
              <Select
                value={filterByOtherParty}
                label="Other Party"
                onChange={(e) => { setFilterByOtherParty(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {/* Get unique other parties from current transactions */}
                {Array.from(new Set(history?.transactions?.map(tx => tx.toUser || tx.fromUser).filter(Boolean) || [])).map((party) => (
                  <MenuItem key={party} value={party}>
                    {party}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 120px', minWidth: 120 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Refresh
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 120px', minWidth: 120 }}>
            <Button
              variant="outlined"
              onClick={handleExportCsv}
              startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              fullWidth
              disabled={isExporting || !history?.transactions?.length}
            >
              Export CSV
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">
              {regularFilter.isExpanded 
                ? `${filteredAndSortedTransactions.length} of ${history?.totalCount || 0} transactions`
                : `${history?.totalCount || 0} transactions found`
              }
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Transactions Table */}
      {/* Columns: Date, Type, Postage, Amount, Balance, Total Fees, Subject (30 chars) */}
      <TableContainer component={Paper}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell width="90">
                    <TableSortLabel
                      active={sortColumn === 'date'}
                      direction={sortColumn === 'date' ? sortDirection : 'desc'}
                      onClick={() => handleSort('date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="180">
                    <TableSortLabel
                      active={sortColumn === 'subject'}
                      direction={sortColumn === 'subject' ? sortDirection : 'desc'}
                      onClick={() => handleSort('subject')}
                    >
                      Subject
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="120">
                    <TableSortLabel
                      active={sortColumn === 'type'}
                      direction={sortColumn === 'type' ? sortDirection : 'desc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="90">
                    <TableSortLabel
                      active={sortColumn === 'postage'}
                      direction={sortColumn === 'postage' ? sortDirection : 'desc'}
                      onClick={() => handleSort('postage')}
                    >
                      Postage
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="100">
                    <TableSortLabel
                      active={sortColumn === 'attached'}
                      direction={sortColumn === 'attached' ? sortDirection : 'desc'}
                      onClick={() => handleSort('attached')}
                    >
                      Attached Credits
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="110">
                    <TableSortLabel
                      active={sortColumn === 'reserved'}
                      direction={sortColumn === 'reserved' ? sortDirection : 'desc'}
                      onClick={() => handleSort('reserved')}
                    >
                      Reserved/Credited
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="80">
                    <TableSortLabel
                      active={sortColumn === 'fees'}
                      direction={sortColumn === 'fees' ? sortDirection : 'desc'}
                      onClick={() => handleSort('fees')}
                    >
                      Fees
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width="90">
                    <TableSortLabel
                      active={sortColumn === 'balance'}
                      direction={sortColumn === 'balance' ? sortDirection : 'desc'}
                      onClick={() => handleSort('balance')}
                    >
                      Balance
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No transactions found for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedTransactions.map((tx: Transaction) => {
                    // Get values with fallback for legacy properties
                    const requiredPostage = tx.requiredPostage ?? tx.postage ?? 0;
                    const attachedFunds = tx.attachedFunds ?? tx.attachedAmount ?? 0;
                    const postageSystemFee = tx.postageSystemFee ?? 0;
                    const attachedFundsFee = tx.attachedFundsFee ?? 0;
                    const systemFeeAmount = tx.systemFeeAmount ?? 0;
                    const totalSystemFees = postageSystemFee + attachedFundsFee + systemFeeAmount;
                    const dateToShow = getTransactionListingDate(tx);
                    
                    // Calculate total reserved/credited (postage + attached)
                    const totalReservedOrCredited = requiredPostage + attachedFunds;
                    const isPending = tx.type === 'Pending Delivery' || tx.type === 'In Inbox';
                    const isWalletNeutralCardDebit = tx.walletBalanceUnchanged === true && systemFeeAmount > 0;
                    // Debit = actual balance went down (amount < 0), or funds are reserved/expired
                    const isDebit = tx.amount < 0 || tx.action === 'Reserved' || tx.action === 'Expired' || isPending;
                    
                    // Color logic:
                    // - Debits (money out / reserved): RED
                    // - Credits (money in / received): GREEN
                    // - Fees are always RED (deductions)
                    const amountColor = isDebit ? '#d32f2f' : '#2e7d32';
                    const feeColor = '#d32f2f'; // Fees are always deductions
                    
                    return (
                      <TableRow 
                        key={tx.transactionId} 
                        hover 
                        onClick={() => handleRowClick(tx)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          {formatDateOnly(dateToShow)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          {truncateSubject(tx.subject, 25)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatTypeDisplay(tx.type)}
                            size="small"
                            color={getTypeColor(tx.type) as any}
                            variant={isPending ? 'filled' : 'outlined'}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: amountColor }}>
                          {requiredPostage > 0 ? `$${requiredPostage.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: amountColor }}>
                          {attachedFunds > 0 ? `$${attachedFunds.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                          {isWalletNeutralCardDebit ? (
                            '-'
                          ) : totalReservedOrCredited > 0 ? (
                            <span style={{ 
                              color: amountColor, 
                              fontWeight: 600 
                            }}>
                              {isDebit ? '-' : '+'}${totalReservedOrCredited.toFixed(2)}
                            </span>
                          ) : (
                            formatAmountWithColor(tx.amount, tx.action)
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: totalSystemFees > 0 ? feeColor : 'text.secondary' }}>
                          {totalSystemFees > 0 ? `-$${totalSystemFees.toFixed(4)}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                          {tx.walletBalanceUnchanged ? (
                            <span title="Qix Credits balance unchanged">
                              ${tx.actualBalanceAfter?.toFixed(2) || '0.00'}
                            </span>
                          ) : (
                            `$${tx.actualBalanceAfter?.toFixed(2) || '0.00'}`
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={history?.totalCount || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </TableContainer>

      {/* Transaction Details Dialog */}
      <TransactionDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        transaction={selectedTransaction}
        onViewMessage={handleViewMessage}
      />
        </>
      )}

      {/* No sub-tabs visible (admin hid both) */}
      {visibleSubTabs.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No sections are currently visible for this page.
        </Typography>
      )}

      {/* Vendor Credits sub-tab */}
      {currentSubTab === 'vendor-credits' && (
        <>
          {/* Per-Sponsor Vendor Credits Summary (dismissible) */}
          {sponsorSummaries.length > 0 && (
            <>
              {!vcSummaryOpen && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ExpandMoreIcon />}
                  onClick={toggleVcSummary}
                  sx={{ mb: 1, textTransform: 'none' }}
                >
                  Show Vendor Credit Summary ({sponsorSummaries.length} vendor{sponsorSummaries.length !== 1 ? 's' : ''})
                </Button>
              )}
              <Collapse in={vcSummaryOpen}>
                <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #56903a 0%, #64903a 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Vendor Credit Accounts
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          ${sponsorSummaries.reduce((sum, g) => sum + g.availableBalance, 0).toFixed(2)}
                        </Typography>
                        <Tooltip title="Hide summary">
                          <IconButton size="small" onClick={toggleVcSummary} sx={{ color: 'white' }}>
                            <ExpandLessIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', borderColor: 'rgba(255,255,255,0.3)' }}>Vendor</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', borderColor: 'rgba(255,255,255,0.3)' }}>Balance</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', borderColor: 'rgba(255,255,255,0.3)' }}>Total Received</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', borderColor: 'rgba(255,255,255,0.3)' }}>Total Expired</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', borderColor: 'rgba(255,255,255,0.3)' }}>Total Spent</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sponsorSummaries
                            .filter(g => g.availableBalance > 0 || showZeroBalanceVendors)
                            .map(g => {
                              const totalExpired = Math.max(0, parseFloat((g.totalGranted - g.availableBalance - g.totalSpent).toFixed(4)));
                              const hasBalance = g.availableBalance > 0;
                              return (
                                <TableRow key={g.sponsor} sx={{ opacity: hasBalance ? 1 : 0.6 }}>
                                  <TableCell sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.15)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      {g.sponsor}
                                      {hasBalance && g.soonestExpiry != null && g.soonestExpiry <= 14 && (
                                        <Chip
                                          label={g.soonestExpiry <= 7
                                            ? `Expires in ${g.soonestExpiry}d`
                                            : `Exp ${g.soonestExpiry}d`}
                                          size="small"
                                          sx={{
                                            bgcolor: g.soonestExpiry <= 7 ? 'warning.main' : 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: '0.65rem',
                                            height: 18,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.15)', fontWeight: 'bold' }}>
                                    ${g.availableBalance.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.15)' }}>
                                    ${g.totalGranted.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.15)' }}>
                                    ${totalExpired.toFixed(2)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.15)' }}>
                                    ${g.totalSpent.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {sponsorSummaries.some(g => g.availableBalance === 0) && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setShowZeroBalanceVendors(v => !v)}
                        startIcon={showZeroBalanceVendors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mt: 1, color: 'rgba(255,255,255,0.75)', textTransform: 'none', fontSize: '0.8rem' }}
                      >
                        {showZeroBalanceVendors ? 'Hide' : 'Show'} vendors with no balance
                        {' '}({sponsorSummaries.filter(g => g.availableBalance === 0).length})
                      </Button>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.65)' }}>
                      Vendor credits can only be used with the issuing vendor and may expire. 2Way Credits can be used anywhere and never expire.
                    </Typography>
                  </CardContent>
                </Card>
              </Collapse>
            </>
          )}

          {/* Promo Transaction History */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Transaction History
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<RefreshIcon />}
                onClick={() => refetchPromo()}
              >
                Refresh
              </Button>
            </Box>
            <TransactionSearchFilter
              transactions={promoHistory || []}
              transactionTypes={promoTransactionTypes}
              {...promoFilter.filterProps}
              csvFilename="promo_transactions"
              csvHeaders={['Transaction ID', 'Date', 'Type', 'Sponsor', 'Campaign', 'Amount', 'From Vendor Credits', 'From 2Way Credits', 'Balance Before', 'Balance After', 'Linked Regular Transaction ID', 'Split Source', 'Description', 'Reference ID']}
              getCsvRow={(tx: any) => [
                tx.transactionId,
                tx.transactionDate,
                tx.transactionType,
                tx.sponsorUsername,
                tx.campaignName || '',
                tx.amount?.toString() || '0',
                (tx.vendorCreditsUsed ?? Math.abs(tx.amount ?? 0)).toString(),
                (tx.regularCreditsUsed ?? 0).toString(),
                (tx.balanceBefore ?? 0).toString(),
                (promoRunningBalances.get(tx.transactionId) ?? tx.balanceAfter ?? 0).toString(),
                tx.linkedRegularTransactionId || '',
                tx.splitSource || '',
                tx.description || '',
                tx.referenceId || ''
              ]}
              filterFn={(tx: any, query, start, end, type) => 
                defaultPromoTransactionFilter(tx, query, start, end, type)
              }
            />
            {promoFilter.isExpanded && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {filteredAndSortedPromoTransactions.length} of {promoHistory?.length || 0} transactions
              </Typography>
            )}
          </Paper>

          <TableContainer component={Paper}>
            {promoLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredAndSortedPromoTransactions.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {(promoHistory?.length ?? 0) > 0 
                    ? 'No transactions match the current filter criteria.'
                    : 'No vendor credit transactions yet.'
                  }
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={promoFilter.sortColumn === 'transactionDate'}
                        direction={promoFilter.sortColumn === 'transactionDate' ? promoFilter.sortDirection : 'desc'}
                        onClick={() => promoFilter.handleSortClick('transactionDate')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={promoFilter.sortColumn === 'transactionType'}
                        direction={promoFilter.sortColumn === 'transactionType' ? promoFilter.sortDirection : 'desc'}
                        onClick={() => promoFilter.handleSortClick('transactionType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={promoFilter.sortColumn === 'sponsorUsername'}
                        direction={promoFilter.sortColumn === 'sponsorUsername' ? promoFilter.sortDirection : 'desc'}
                        onClick={() => promoFilter.handleSortClick('sponsorUsername')}
                      >
                        Sponsor
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={promoFilter.sortColumn === 'campaignName'}
                        direction={promoFilter.sortColumn === 'campaignName' ? promoFilter.sortDirection : 'desc'}
                        onClick={() => promoFilter.handleSortClick('campaignName')}
                      >
                        Campaign
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      <TableSortLabel
                        active={promoFilter.sortColumn === 'amount'}
                        direction={promoFilter.sortColumn === 'amount' ? promoFilter.sortDirection : 'desc'}
                        onClick={() => promoFilter.handleSortClick('amount')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      <TableSortLabel
                        active={promoFilter.sortColumn === 'balanceAfter'}
                        direction={promoFilter.sortColumn === 'balanceAfter' ? promoFilter.sortDirection : 'desc'}
                        onClick={() => promoFilter.handleSortClick('balanceAfter')}
                      >
                        Balance After
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedPromoTransactions.map((tx) => (
                    <TableRow 
                      key={tx.transactionId} 
                      onClick={() => handlePromoRowClick(tx)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' } 
                      }}
                    >
                      <TableCell>
                        {fmtDateTimeShort(tx.transactionDate)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={tx.transactionType.replace(/_/g, ' ')}
                          color={
                            tx.transactionType === 'GRANT' || tx.transactionType === 'ADDITION' 
                              ? 'success' 
                              : tx.transactionType === 'SPEND' 
                                ? 'warning' 
                                : tx.transactionType === 'REFUND' 
                                  ? 'info' 
                                  : tx.transactionType === 'EXPIRE'
                                    ? 'error'
                                    : 'default'
                          }
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>{tx.sponsorUsername}</TableCell>
                      <TableCell>{tx.campaignName}</TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          color: tx.transactionType === 'SPEND' || tx.transactionType === 'EXPIRE' 
                            ? 'error.main' 
                            : 'success.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {tx.transactionType === 'SPEND' || tx.transactionType === 'EXPIRE' ? '-' : '+'}${Math.abs(tx.amount).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                        ${(promoRunningBalances.get(tx.transactionId) ?? tx.balanceAfter)?.toFixed(2) ?? '0.00'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tx.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePromoRowClick(tx);
                            }}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Vendor
                          </Link>
                          {tx.linkedRegularTransactionId && (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenLinkedRegularDetail(tx.linkedRegularTransactionId!);
                              }}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              2Way
                            </Link>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </>
      )}

      {/* Promo Transaction Details Dialog */}
      <PromoTransactionDetailsDialog
        open={promoDetailsDialogOpen}
        onClose={handleClosePromoDetails}
        transaction={selectedPromoTransaction}
        onOpenLinkedTransaction={handleOpenLinkedRegularDetail}
      />

      {/* Add Funds tab */}
      {currentSubTab === 'add-funds' && (
        <AddFundsTab
          addFunds={addFundsWithBalanceRefresh}
          getAutoReplenish={accountService.getAutoReplenish.bind(accountService)}
          saveAutoReplenish={accountService.saveAutoReplenish.bind(accountService)}
          listPaymentMethods={accountService.listPaymentMethods.bind(accountService)}
          setupCardPaymentMethod={accountService.setupCardPaymentMethod.bind(accountService)}
          setupBankPaymentMethod={accountService.setupBankPaymentMethod.bind(accountService)}
          minimumAmount={MIN_FUNDS_AMOUNT}
        />
      )}
    </Container>
  );
};

export default AccountHistory;
